// apps/provider/src/services/api/alerts.js
// Persisted patient alerts (Xano table patient_alert). Written by the form-submit
// path (forms.settings.form.alertRules); read by the PatientDetails alert stack,
// the calendar, and the Home schedule.
//
// Methods: getPatientAlerts, getPatientAlertsBatch, resolvePatientAlert
//
// Response types: @prism-ehr/shared → PatientAlertResponse, PatientAlertBatchRow

/**
 * @returns {Object} Patient alert domain methods (bound to `this` of the composed apiService)
 */
export function createAlertMethods() {
  return {
    /**
     * Persisted alerts for one patient. Unresolved only unless includeResolved.
     *
     * Xano: GET /patient-alerts
     * Xano file: patient-alerts_GET.xs
     *
     * @param {string} patientId
     * @param {{ includeResolved?: boolean }} [options]
     * @returns {Promise<import('@prism-ehr/shared').PatientAlertResponse[]>}
     */
    async getPatientAlerts(patientId, { includeResolved = false } = {}) {
      const params = new URLSearchParams({ patient_id: patientId });
      if (includeResolved) params.append('include_resolved', 'true');
      return this.request(`/patient-alerts?${params}`, { method: 'GET' });
    },

    /**
     * Unresolved, calendar-visible alerts for a batch of patients (calendar +
     * Home badges). Flat list; callers group by patient_id.
     *
     * Xano: POST /patient-alerts-batch (GET twin kept byte-identical, no caller)
     * Xano file: patient-alerts-batch_POST.xs
     *
     * @param {string[]} patientIds - chunk at 100 per call
     * @returns {Promise<import('@prism-ehr/shared').PatientAlertBatchRow[]>}
     */
    async getPatientAlertsBatch(patientIds) {
      return this.request('/patient-alerts-batch', {
        method: 'POST',
        body: JSON.stringify({ patient_ids: (patientIds || []).join(',') }),
      }, 'getPatientAlertsBatch');
    },

    /**
     * Mark one alert resolved by the current user.
     *
     * Xano: PATCH /patient-alerts/{patient_alert_id}/resolve
     * Xano file: patient-alerts_patient_alert_id_resolve_PATCH.xs
     *
     * @param {string} alertId
     * @returns {Promise<import('@prism-ehr/shared').PatientAlertResponse>}
     */
    async resolvePatientAlert(alertId) {
      return this.request(`/patient-alerts/${alertId}/resolve`, { method: 'PATCH' });
    },
  };
}
